import {
  CaretDoubleLeftIcon,
  CaretDoubleRightIcon,
  DatabaseIcon,
  DownloadSimpleIcon,
  FileTextIcon,
  FoldersIcon,
  SignOutIcon,
} from '@phosphor-icons/react'
import { useState } from 'react'
import { Button } from '@cloudflare/kumo/components/button'
import { NavLink } from 'react-router-dom'
import BrandMark from '../common/BrandMark'
import { useAuth } from '../../hooks/useAuth'

export default function AppShell({ children }) {
  const { user, logout, demoMode } = useAuth()
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem('press-pass-sidebar-collapsed') === 'true'
    } catch {
      return false
    }
  })
  const initials = user.displayName
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase()

  function toggleSidebar() {
    setCollapsed((current) => {
      const next = !current
      try {
        localStorage.setItem('press-pass-sidebar-collapsed', String(next))
      } catch {
        // The sidebar still works if localStorage is unavailable.
      }
      return next
    })
  }

  const navClass = ({ isActive }) =>
    `flex min-h-11 items-center rounded-lg py-3 font-display text-sm font-bold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-yellow max-md:flex-1 max-md:flex-col max-md:gap-0.5 max-md:px-1 max-md:py-1 max-md:text-[0.62rem] ${
      collapsed ? 'justify-center px-2' : 'gap-3 px-3'
    } ${
      isActive
        ? 'bg-yellow text-ink'
        : 'text-[#aaa59e] hover:bg-white/5 hover:text-white'
    }`

  const labelClass = collapsed ? 'max-md:inline md:hidden' : ''

  return (
    <div className={`min-h-screen transition-[padding] duration-200 max-md:pb-17 max-md:pl-0 ${collapsed ? 'pl-[76px]' : 'pl-[244px]'}`}>
      <aside className={`fixed inset-y-0 left-0 z-50 flex flex-col bg-ink py-7 text-white transition-[width,padding] duration-200 max-md:inset-x-0 max-md:top-auto max-md:h-16 max-md:w-full max-md:flex-row max-md:items-center max-md:border-t max-md:border-[#393633] max-md:px-3 max-md:py-2 ${collapsed ? 'w-[76px] px-3' : 'w-[244px] px-5'}`}>
        <div
          className={`border-b border-[#393633] pb-6 max-md:hidden ${collapsed ? 'px-1' : ''}`}
          title={collapsed ? 'HNTM PreNexus' : undefined}
        >
          <BrandMark compact />
        </div>
        <nav
          className="mt-7 flex flex-col gap-2 max-md:mt-0 max-md:w-full max-md:flex-row max-md:justify-around"
          aria-label="Navigasi utama"
        >
          <NavLink
            to="/forms"
            className={navClass}
            title="Form"
          >
            <FileTextIcon size={19} weight="bold" />
            <span className={labelClass}>Form</span>
          </NavLink>
          <NavLink
            to="/files"
            className={navClass}
            title="Files"
          >
            <FoldersIcon size={19} weight="bold" />
            <span className={labelClass}>Files</span>
          </NavLink>
          <NavLink
            to="/resources"
            className={navClass}
            title="Client resources"
            aria-label="Client resources"
          >
            <DatabaseIcon size={19} weight="bold" />
            <span className={labelClass}>
              <span className="max-md:hidden">Client resources</span>
              <span className="hidden max-md:inline">Resources</span>
            </span>
          </NavLink>
          {user.role === 'admin' ? (
            <NavLink to="/export-data" className={navClass} title="Export data">
              <DownloadSimpleIcon size={19} weight="bold" />
              <span className={labelClass}>Export data</span>
            </NavLink>
          ) : null}
        </nav>
        <div className="mt-auto border-t border-[#393633] pt-4 max-md:hidden">
          {demoMode ? (
            <span className="mb-4 inline-block rounded border border-[#5b554f] px-2 py-1 font-display text-[0.62rem] font-bold tracking-[0.16em] text-yellow uppercase">
              Mode demo
            </span>
          ) : null}
          <div className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2.5'}`} title={collapsed ? `${user.displayName} — ${user.email}` : undefined}>
            <span className="grid size-9 shrink-0 place-items-center rounded-full bg-yellow font-display text-xs font-extrabold text-ink">
              {initials}
            </span>
            <div className={`min-w-0 ${collapsed ? 'hidden' : ''}`}>
              <strong className="block truncate text-sm">{user.displayName}</strong>
              <small className="mt-0.5 block truncate text-[0.69rem] text-[#8f8982]">
                {user.email}
              </small>
            </div>
          </div>
          <Button
            variant="ghost"
            icon={SignOutIcon}
            onClick={logout}
            aria-label="Keluar"
            title="Keluar"
            className={`mt-3! w-full! text-[#aaa59e]! ${collapsed ? 'justify-center!' : 'justify-start!'}`}
          >
            <span className={collapsed ? 'hidden' : ''}>Keluar</span>
          </Button>
          <Button
            type="button"
            variant="ghost"
            shape="square"
            icon={collapsed ? CaretDoubleRightIcon : CaretDoubleLeftIcon}
            aria-label={collapsed ? 'Buka sidebar' : 'Tutup sidebar'}
            title={collapsed ? 'Buka sidebar' : 'Tutup sidebar'}
            onClick={toggleSidebar}
            className={`mt-2! text-[#aaa59e]! ${collapsed ? 'mx-auto!' : ''}`}
          />
        </div>
      </aside>
      <header
        className="sticky top-0 z-40 hidden items-center gap-3 border-b border-[#393633] bg-ink px-4 py-2.5 text-white shadow-sm max-md:flex"
        aria-label="Akun pengguna"
      >
        <span className="grid size-9 shrink-0 place-items-center rounded-full bg-yellow font-display text-xs font-extrabold text-ink">
          {initials}
        </span>
        <div className="min-w-0 flex-1">
          <strong className="block truncate text-sm">{user.displayName}</strong>
          <small className="mt-0.5 block truncate text-[0.69rem] text-[#aaa59e]">
            {user.email}
          </small>
        </div>
        <Button
          variant="ghost"
          icon={SignOutIcon}
          onClick={logout}
          aria-label="Keluar"
          title="Keluar"
          className="shrink-0! text-[#aaa59e]!"
        >
          Keluar
        </Button>
      </header>
      <main className="min-w-0">{children}</main>
    </div>
  )
}
